import React, { useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { Box } from '@mui/material';

/*
  ScrollArea: a vertically scrollable box with a custom, fixed-height scroll
  thumb. Native scrolling (wheel / trackpad / keyboard / touch) is preserved;
  the native scrollbar is hidden and replaced with an overlay thumb that is the
  SAME height on every instance — so a set of cards show identical scroll
  handles regardless of how much content each one holds. The thumb is draggable.

  Props:
   - children: scrollable content
   - sx: style overrides applied to the inner scroller (e.g. padding)
*/

const THUMB_HEIGHT = 44; // fixed thumb height — uniform across every instance
const GUTTER = 6;        // space reserved above/below the thumb within the track

export default function ScrollArea({ children, sx }) {
  const scrollerRef = useRef(null);
  const contentRef = useRef(null);
  const [m, setM] = useState({ overflow: 0, travel: 0, scrollTop: 0 });

  // Read the scroll geometry; bail out of a state update when nothing changed
  // so a ResizeObserver tick can't trigger a render loop.
  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const overflow = Math.max(0, el.scrollHeight - el.clientHeight);
    const travel = Math.max(0, el.clientHeight - 2 * GUTTER - THUMB_HEIGHT);
    const scrollTop = el.scrollTop;
    setM((prev) =>
      prev.overflow === overflow && prev.travel === travel && prev.scrollTop === scrollTop
        ? prev
        : { overflow, travel, scrollTop }
    );
  }, []);

  // Measure before paint so the thumb is correct on first render.
  useLayoutEffect(() => { measure(); }, [measure]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => measure();
    el.addEventListener('scroll', onScroll, { passive: true });
    // Re-measure when the viewport (el) or the content (contentRef) resizes.
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [measure]);

  const visible = m.overflow > 1;
  const thumbTop = GUTTER + (m.overflow > 0 ? (m.scrollTop / m.overflow) * m.travel : 0);

  const handleThumbDown = useCallback((e) => {
    e.preventDefault();
    const el = scrollerRef.current;
    if (!el) return;
    const startY = e.clientY;
    const startScroll = el.scrollTop;
    const { overflow, travel } = m;
    const onMove = (ev) => {
      if (travel <= 0) return;
      el.scrollTop = startScroll + ((ev.clientY - startY) / travel) * overflow;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [m]);

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
      <Box
        ref={scrollerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          // Hide the native scrollbar — the overlay thumb replaces it.
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          ...sx,
        }}
      >
        <div ref={contentRef}>{children}</div>
      </Box>
      {visible && (
        <Box
          onMouseDown={handleThumbDown}
          sx={{
            position: 'absolute',
            top: `${thumbTop}px`,
            right: 3,
            width: 8,
            height: `${THUMB_HEIGHT}px`,
            borderRadius: 4,
            bgcolor: 'rgba(15,23,42,0.45)',
            cursor: 'grab',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(15,23,42,0.65)' },
            '&:active': { cursor: 'grabbing', bgcolor: 'rgba(15,23,42,0.72)' },
          }}
        />
      )}
    </Box>
  );
}
